import { useEffect, useState, useRef  } from 'react';
import '../../dmView/style/App.css';
import SearchColumn from './Searching/SearchColumn.js';
import Home from './Home.js';
import EncounterColumn from './EncounterColumn/EncounterColumn';
import SideMenu from './SideMenu/SideMenu.js';
import { backendUrl, generateUniqueId, INIT_ENCOUNTER, SHORT_REFRESH, 
        sortCreatureArray, sortEncounters, homebrewTemplate, shuffledMonsterList,
        conditionImgMap, getVideoLink } from '../constants';
import YouTubeEmbed from './EncounterColumn/YouTubeEmbed.js';
import io from 'socket.io-client';
import { ImportDndBeyondCharacters } from '../api/ImportDndBeyondCharacters'
import defaultBackgroundImg from '../pics/backgrounds/happyTavern.png'
import { useUser } from '../../../../providers/UserProvider.js';
import UploadMonsterImage from './EncounterColumn/UploadMonsterImage.js';
import { useImportedPlayers } from '../../../../providers/ImportedPlayersProvider';
import IdlePopUp from './IdlePopUp.js';
import axios from 'axios';

function findCreatureIndexes(currentEncounterCreatures, reassignSelected) {
    return reassignSelected.map(guid =>
        currentEncounterCreatures.findIndex(c => c.creatureGuid === guid)
    ).filter(index => index !== -1); // Filter out any not found
}

const DmView = () => {
    const [defaultBackgroundOverride, setDefaultBackgroundOverride] = useState(window.localStorage.getItem("defaultBackground") === "true");
    const [playerViewBackground, setPlayerViewBackground] = useState({type: "image", src: defaultBackgroundImg});
    const [currentEncounter, setCurrentEncounter] = useState(INIT_ENCOUNTER);
    const [onFirstLoad, setOnFirstLoad] = useState(true);
    const [refreshLoading, setRefreshLoading] = useState(false);
    const [autoRefreshDndbPlayers, setAutoRefreshDndbPlayers] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [refreshCheck, setRefreshCheck] = useState(false);
    const [showSearchList, setShowSearchList] = useState(true);
    const [encounterGuid, setEncounterGuid] = useState(INIT_ENCOUNTER.encounterGuid);
    const [savedEncounters, setSavedEncounters] = useState([]);
    const [uploadIconMenu, setUploadIconMenu] = useState(false); 
    const [uploadIconCreature, setUploadIconCreature] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState([]);
    const [showIdlePopup, setShowIdlePopup] = useState(false);
    const [searchSelectedCreature, setSearchSelectedCreature] = useState(null);

    const [socket, setSocket] = useState(null);
    const [connectedToServer, setConnectedToServer] = useState(false)

    const { username, sessionID } = useUser();
    const {addImportedPlayers} = useImportedPlayers();

    const selectedIndexRef = useRef(selectedIndex);
    const usernameRef = useRef(username);
    const sessionIdRef = useRef(sessionID);
    const encounterGuidRef = useRef(currentEncounter.encounterGuid);
    const encounterNameRef = useRef(currentEncounter.encounterName);
    const firstLoadRef = useRef(onFirstLoad);
    const socketRef = useRef(null)

    useEffect(() => {
        sessionIdRef.current = sessionID;
    }, [sessionID]);

    useEffect(() => {
        usernameRef.current = username;
    }, [username]);

    useEffect(() => {
        encounterGuidRef.current = currentEncounter.encounterGuid;
    }, [currentEncounter.encounterGuid]);
    
    useEffect(() => {
        encounterNameRef.current = currentEncounter.encounterName;
    }, [currentEncounter.encounterName]);

    useEffect(() => {
        selectedIndexRef.current = selectedIndex;
    }, [selectedIndex]);

    useEffect(() => {
        firstLoadRef.current = onFirstLoad;
    }, [onFirstLoad]);

    useEffect(() => {
        if(username === 'Username') {
            setPlayerViewBackground({type: "image", src: defaultBackgroundImg})
            setSavedEncounters([])
            setCurrentEncounter(INIT_ENCOUNTER)
            setOnFirstLoad(true)
            setAutoRefreshDndbPlayers(false)
            setAutoRefresh(false)
            setRefreshCheck(false)
            setEncounterGuid('')
        }
    }, [username]);

    useEffect(() => {
        if (!socketRef.current) {
            socketRef.current = io(backendUrl, {
                transports: ["websocket", "polling"],
                reconnectionDelay: 1000,
                reconnection: true,
                reconnectionAttempts: 10,
            });
            setSocket(socketRef.current);
        }

        const socket = socketRef.current;
        if (!socket) return;

        let socketDisconnect = () => {
            socket.emit("logDisconnect", "DmView Idle")
            setShowIdlePopup(true)
            socket.disconnect()
        }

        const SIX_HOURS = 6 * 3600000
        let activityTimeout = setTimeout(socketDisconnect, SIX_HOURS);

        const resetTimer = () => {
            clearTimeout(activityTimeout);
            activityTimeout = setTimeout(socketDisconnect, SIX_HOURS);
        };

        window.addEventListener("mousedown", resetTimer);

        let disconnectTimer = null;

        console.log('Socket initialized:', socket);
        console.log('Available transports:', socket.io.engine.transports);

        socket.on('connect', () => {
            console.log(`Connected to DmView with transport: ${socket.io.engine.transport.name}`);
            socket.emit('connectDmView', username);
            setConnectedToServer(true);
        });

        socket.on('connect_error', (error) => {
            console.error('Socket connection error:', error);
            console.error('Error message:', error.message);
        });

        // Log reconnect attempts
        socket.on('reconnect_attempt', (attemptNumber) => {
            console.log(`Socket.IO reconnect attempt #${attemptNumber}`);
            socket.emit("logReconnectAttempt", username, attemptNumber);
        });

        socket.on('sendSavedEncounters', (encountersResponse) => {
            sortEncounters(encountersResponse)                
            setSavedEncounters(encountersResponse)

            const lastEncounterGuid = localStorage.getItem('lastEncounterGuid');
            if(lastEncounterGuid) {
                const lastEncounter = encountersResponse.find(encounter => encounter.encounterGuid === lastEncounterGuid);
                if(lastEncounter) {
                    handleLoadEncounter(lastEncounter);
                }
            }
        });

        socket.on('dmViewPing', () => {
            if(!firstLoadRef.current) {
                let payload = {
                    src: "DmView",
                    username: usernameRef.current,
                    socketId: socket.id,
                    sessionId: sessionIdRef.current,
                    encounterName: encounterNameRef?.current,
                    encounterGuid: encounterGuidRef?.current,
                }
                axios.post(`${backendUrl}/keepalive`, payload)
            }
        });

            // Clean up the socket connection and timer on component unmount
        return () => {
            if (disconnectTimer) clearTimeout(disconnectTimer);
            if(socket) { 
                socket.emit("logDisconnect", "DmView Unmount")
                clearTimeout(activityTimeout);
                window.removeEventListener("mousedown", resetTimer);
                socket.disconnect(); 
            }
        };
    }, []);
    
    useEffect(() => {
        if(refreshCheck) {
            const timer = setTimeout(() => {
                setRefreshCheck(false)
            }, 3500); 
            return () => clearInterval(timer);
        }
    }, [refreshCheck])

    const refreshPlayerProfiles = async () => {
        // Only Refresh when socket is connected
        if(socketRef.current && socketRef.current.connected) {
            try {
                console.log("Refreshing Players")
                console.log("----------------")
                // Get player stats for HP for players from dnd beyond
                // Keep isPlayer check so dms can import character without auto refresh
                const filteredPlayers = currentEncounter.creatures.filter(creature => 
                    creature.type === 'player' && creature.dnd_b_player_id && creature.dnd_b_player_id[0] !== '!' && creature.dnd_b_player_id !== 'preset');

                const playerIds = filteredPlayers.map(player => player.dnd_b_player_id.toString()); // Map to get the ids as strings
                const playerNames = filteredPlayers.map(player => player.name); // Map to get the ids as strings
                const refreshedData = await ImportDndBeyondCharacters(playerIds, currentEncounter.encounterGuid, undefined, username, playerNames, false);
                
                // Iterate over the first array using a for loop
                let updatedDifCreatures = [] 
                const updatedCreatures = currentEncounter.creatures.map(creature => {
                    const refreshedPlayer = refreshedData.find(data => { 
                        return data.dnd_b_player_id === creature.dnd_b_player_id
                    });

                    // If the current creature is found in the refreshedData list then refresh data but keep a few of the old stuff
                    if (refreshedPlayer) {
                        const diffKeys = ["hit_points", "hit_points_current", "hit_points_default", "hit_points_modifier", "hit_points_override", "hit_points_temp",
                                          "avatarUrl", "death_saves_success_count", "death_saves_failure_count", "exhaustionLvl", "inspiration", "bardic_inspiration", "name", "armor_class", "inventory"];
                        
                        let rEffects = refreshedPlayer.effectObjs;
                        // Only find conditions since they are the only effects players can update
                        let cEffects = creature.effectObjs.filter(e => e.effectName.includes("Condition"));
                        
                        let effectDiff = rEffects.length !== cEffects.length || !rEffects.every((item, i) => item.effectName === cEffects[i].effectName);
                        
                        const mergedEffects = [...cEffects];
                        rEffects.forEach(e => {
                            if (!mergedEffects.some(existing => existing.effectName === e.effectName))  {
                                mergedEffects.push(e);
                            } 
                        });

                        const conditionKeys = new Set(Object.keys(conditionImgMap));
                        for(let i = 0; i < mergedEffects.length; i++) {
                            if (!rEffects.some(existing => existing.effectName === mergedEffects[i].effectName) && conditionKeys.has(mergedEffects[i].effectName)) {
                                // remove from mergedEffects then recheck the index (i--)
                                mergedEffects.splice(i, 1);
                                i--;
                            }
                        }

                        for (const key of diffKeys) {
                            if (creature[key] !== refreshedPlayer[key] || effectDiff) {
                                console.log("Updating: " + key + " | EffectDiff: " + effectDiff)
                                creature = {
                                    ...refreshedPlayer,
                                    creatureGuid: creature.creatureGuid, // original guid
                                    effectObjs: mergedEffects,
                                    initiative: creature.initiative, // might remove this in the future for auto initiative in the dnd_b app
                                }

                                updatedDifCreatures.push(creature)
                                break;
                            }
                        }
                    }

                    //This will update the creature in the import list if needed
                    return creature
                });

                if(updatedDifCreatures.length > 0) { 
                    console.log("Update Found for: ", updatedDifCreatures.map(c => c.name).join(", ")); 
                    socket.emit("updateDndBPlayers", updatedDifCreatures)
                    
                    addImportedPlayers(updatedDifCreatures)
                } else { 
                    console.log("No updates found...") 
                }

                console.log("Players Refreshed!")
                console.log("----------------")

                return updatedCreatures;
            } catch (error) {
                console.warn(error)
            }  
        }
    };

    const resort = (refreshedCreatures = undefined) => {
        const sortCreatures = refreshedCreatures || currentEncounter.creatures;
        let reassignSelected = [];
        // Use the ref so its not stale when called from useEffect
        const latestSelectedIndex = selectedIndexRef.current;

        if (latestSelectedIndex.length !== 0) {
            latestSelectedIndex.forEach(indexObj => {
                reassignSelected.push(sortCreatures[indexObj.index].creatureGuid);
            });
        }
        const sortedCreatures = sortCreatureArray(sortCreatures);
        setCurrentEncounter(prev => ({ ...prev, creatures: [...sortedCreatures] }));

        // This handles open statblocks while the index of creatures change
        const postSortedIndices = findCreatureIndexes(sortedCreatures, reassignSelected);

        setSelectedIndex((prev) => {
            prev.forEach((indexObj, i) => {
                indexObj.index = postSortedIndices[i];
            });
            return prev;
        });
    };

    const handleRefresh = async () => {
        setRefreshLoading(true)

        if (autoRefreshDndbPlayers) {
            const refreshed = await refreshPlayerProfiles()
            resort(refreshed)
        }

        // Set this for a minimum animation spin of 1 seconds
        setTimeout(() => {
            setRefreshLoading(false)
            setRefreshCheck(true);
        }, 1000); 
    };

    useEffect(() => {
        if(onFirstLoad && currentEncounter.encounterGuid !== "" && !window.location.href.includes("/playerView")) {
            setOnFirstLoad(false)
        }

        setSavedEncounters((prevEncounters) =>
            prevEncounters.map((encounter) =>
              encounter.encounterGuid === currentEncounter.encounterGuid
                ? { ...encounter, ...currentEncounter } 
                : encounter 
            )
        );

        // Check current encounter to see if we need to auto refresh from DNB_B
        if(currentEncounter.creatures) {
            let foundPlayer = false;

            // If they have the '!' auto sync is disabled
            currentEncounter.creatures.forEach(creature => {
                if(creature.from === "dnd_b" && creature.dnd_b_player_id && creature.dnd_b_player_id[0] !== '!' && creature.type === "player") {
                    foundPlayer = true;
                }
            });
            
            setAutoRefreshDndbPlayers(foundPlayer)
            setAutoRefresh(foundPlayer)
        }

        let intervalId = 0
        // Refresh every 1 or 5 minutes
        if (autoRefreshDndbPlayers) {
            let refreshTimer = SHORT_REFRESH;
            console.log("Auto Refresh in Minutes", refreshTimer)

            intervalId = setInterval(() => {
                if(autoRefreshDndbPlayers) {
                    handleRefresh();
                    console.log("🔄AUTO-REFRESH DND_B PLAYERS🔄")
                }

            // X minutes in milliseconds
            }, refreshTimer * 60000.0); 
        }

        // Cleanup interval on component unmount
        return () => clearInterval(intervalId);
    // eslint-disable-next-line
    }, [currentEncounter]);

    useEffect(() => {
        setCurrentEncounter({...currentEncounter, backgroundGuid: playerViewBackground.src})
    }, [playerViewBackground]);

    const handleNewEncounter = (action='new') => {
        let newGuid = generateUniqueId();
        if(action === 'copy') {
            console.log("%c=== Copy Encounter ===", "background: green;", newGuid)
            let copy = {
                ...currentEncounter,
                encounterGuid: newGuid,
                encounterName: currentEncounter.encounterName + ' - COPY',
                username: username,
                creatures: currentEncounter.creatures.map(c => ({
                    ...c,
                    encounterGuid: newGuid,
                    creatureGuid: generateUniqueId(),
                    encounterName: currentEncounter.encounterName + ' - COPY'
                }))
            }; 

            socket.emit("newEncounter", copy.encounterGuid, copy.encounterName, copy.folder_dir)
            
            setTimeout(() => {
                copy.creatures.forEach(c => {
                    socket.emit("addCreatureToEncounter", c)
                });

                socket.emit("encounterBackgroundChange", {imageGuid: copy.backgroundGuid}, copy.encounterGuid)

                let sOptions = JSON.stringify(copy.playerViewOptions)
                let sInitOptions = JSON.stringify(INIT_ENCOUNTER.playerViewOptions)

                if(sOptions !== sInitOptions) {
                    socket.emit("editWholePvOptions", copy.playerViewOptions, copy.encounterGuid)
                }
            }, 500)
            
            setSavedEncounters([...savedEncounters, copy])
            setCurrentEncounter(copy)
        } else {
            console.log("%c=== New Encounter ===", "background: green;", newGuid)
            setCurrentEncounter({...INIT_ENCOUNTER, encounterGuid: newGuid, username: username, folder_dir: currentEncounter.folder_dir})
            setPlayerViewBackground({type: 'image', src: defaultBackgroundImg})
        }

        setEncounterGuid(newGuid)
        window.localStorage.setItem("lastEncounterGuid", newGuid)
    };  

    const handleUploadMonsterImage = (event, creature, column = "search") => {
        event.stopPropagation()
        setUploadIconMenu(true)
        //add coulmn to the creature so we know where to upload the image
        setUploadIconCreature({...creature, column: column})
    }

    const handleLoadEncounter = (encounter) => {
        console.log("%cLoaded: " + encounter.encounterName + ' ' + encounter.encounterGuid + ' - ' + encounter.folder_dir + ' - ', "background: #fdfd96;")

        setCurrentEncounter({...encounter, creatures: sortCreatureArray(encounter.creatures)})
        setEncounterGuid(encounter.encounterGuid)

        if(encounter.backgroundGuid === 'default') {
            setPlayerViewBackground({type: "image", src: defaultBackgroundImg})
        } else {
            setPlayerViewBackground(
                encounter.backgroundGuid.includes('youtube.com')
                ? getVideoLink(encounter.backgroundGuid)
                : {type: 'image', src: encounter.backgroundGuid}
            )
        }
    };      

    const addNewHomebrew = async (e, where='dmview') => {
        let newHomebrew = null
        if(where === 'dmview')  {
            newHomebrew = {...homebrewTemplate, creatureGuid: generateUniqueId()} 
        } else {

            if(shuffledMonsterList[0].avatarUrl === null) {
                // Wait for avatarUrl to be set (polling every 100ms, max 5s)
                const waitForAvatarUrl = async (obj, timeout = 5000, interval = 100) => {
                    const start = Date.now();
                    while (!obj.avatarUrl) {
                        if (Date.now() - start > timeout) break;
                        await new Promise(res => setTimeout(res, interval));
                    }
                };

                await waitForAvatarUrl(shuffledMonsterList[0]);
            }

            newHomebrew = {
                ...shuffledMonsterList[0], 
                name: 'Template - ' + shuffledMonsterList[0].name,
                name_default: "Homebrew-Template-DmBuddy", // used to auto set edit mode to true
                creatureGuid: generateUniqueId()
            }         
        }
       
        setSearchSelectedCreature(newHomebrew)
    }

    return (
        <div className="dmViewbackground" style={{
                backgroundImage: (defaultBackgroundOverride || (playerViewBackground.type === "image" && playerViewBackground.src))
                    ? `url(${defaultBackgroundOverride ? defaultBackgroundImg : playerViewBackground.src})` 
                    : 'none'
            }}
        >
            {playerViewBackground.type === "youtube" && !defaultBackgroundOverride && 
                <YouTubeEmbed embedUrl={playerViewBackground.src} page={'dmView'}/>
            }

            {showIdlePopup && 
                <IdlePopUp page={'DmView'}/>
            }

            { onFirstLoad ? ( 
                <Home connectedToServer={connectedToServer} savedEncounters={savedEncounters} setSavedEncounters={setSavedEncounters}  currentEncounter={currentEncounter} setCurrentEncounter={setCurrentEncounter} encounterGuid={encounterGuid} handleNewEncounter={handleNewEncounter} handleLoadEncounter={handleLoadEncounter} setSearchSelectedCreature={setSearchSelectedCreature} addNewHomebrew={addNewHomebrew} socket={socket}/>
            ) : ( 
                <>  
                    <SideMenu showSearchList={showSearchList} setShowSearchList={setShowSearchList} socket={socket}/>
                    {showSearchList &&  
                        <SearchColumn setCurrentEncounter={setCurrentEncounter} encounterGuid={encounterGuid} handleUploadMonsterImage={handleUploadMonsterImage} uploadIconCreature={uploadIconCreature} setShowSearchList={setShowSearchList} searchSelectedCreature={searchSelectedCreature} setSearchSelectedCreature={setSearchSelectedCreature} addNewHomebrew={addNewHomebrew} socket={socket}/>
                    }
                    <EncounterColumn currentEncounter={currentEncounter} savedEncounters={savedEncounters} refreshLoading={refreshLoading} setPlayerViewBackground={setPlayerViewBackground} setDefaultBackgroundOverride={setDefaultBackgroundOverride} setSavedEncounters={setSavedEncounters} refreshCheck={refreshCheck} autoRefresh={autoRefresh} setCurrentEncounter={setCurrentEncounter} handleRefresh={handleRefresh} setEncounterGuid={setEncounterGuid} handleNewEncounter={handleNewEncounter} showSearchList={showSearchList} handleLoadEncounter={handleLoadEncounter} setUploadIconMenu={setUploadIconMenu} setUploadIconCreature={setUploadIconCreature} handleUploadMonsterImage={handleUploadMonsterImage} resort={resort} selectedIndex={selectedIndex} setSelectedIndex={setSelectedIndex} socket={socket}/>
                </>
            )}
            <UploadMonsterImage setCurrentEncounter={setCurrentEncounter} uploadIconMenu={uploadIconMenu} setUploadIconCreature={setUploadIconCreature} setUploadIconMenu={setUploadIconMenu} uploadIconCreature={uploadIconCreature} socket={socket}/>
        </div>
    );
};

export default DmView;